import { Container } from '@mui/material';
import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/container/navbar';
import { ThemeContext } from '../../contexts/theme-context';
import './stm32g030-controller-board.css';

const powerRails = [
  ['VDC5', 'USB-C VBUS', 'About 5 V'],
  ['+3.3V', 'AP2112K-3.3 output', 'About 3.3 V'],
];

const programmingOptions = [
  ['UART bootloader', 'USB-C through CH340C', 'BOOT0 high', 'Simple flashing without a debug probe'],
  ['SWD', 'J3 SWD header', 'BOOT0 not required', 'Debugging, recovery, and production programming'],
];

const j4Pins = [
  ['1', 'PB9', '9', 'PRO_USB_TX'],
  ['2', 'PC14 / OSC32_IN', '10', 'PRO_USB_RX'],
  ['3', 'PC15 / OSC32_OUT', '11', 'PA4'],
  ['4', '+3.3V', '12', 'PA5'],
  ['5', 'GND', '13', 'PA6'],
  ['6', 'NRST', '14', 'PA7'],
  ['7', 'PA0', '15', 'PB0'],
  ['8', 'PA1', '16', 'PB1 / USER LED'],
];

const j5Pins = [
  ['1', 'PB2', '9', 'SWDIO'],
  ['2', 'PA8', '10', 'BOOT0'],
  ['3', 'PA9', '11', 'PA15'],
  ['4', 'PC6', '12', 'PB3'],
  ['5', 'PA10', '13', 'PB4'],
  ['6', 'PA11', '14', 'PB5'],
  ['7', 'PA12', '15', 'PB6'],
  ['8', 'Unlabeled in source', '16', 'PB7 / PB8 area'],
];

const checks = [
  'Inspect USB-C, regulator, MCU, and CH340C soldering before powering the board.',
  'Connect USB-C and confirm the power LED turns on.',
  'Measure +3.3V at the header before attaching external circuits.',
  'Press reset and confirm the board remains stable.',
];

const troubleshooting = [
  ['Power LED is off', 'Check USB cable, VDC5, USB-C soldering, and AP2112K input.'],
  ['No serial port appears', 'Use a data-capable USB cable and confirm CH340 driver support.'],
  ['UART flashing fails', 'Set J2 pins 1-2, reset after setting BOOT0, and free PA2/PA3 from external loads.'],
  ['SWD cannot connect', 'Check 3.3 V reference, SWDIO/SWCLK order, NRST, and connect-under-reset.'],
  ['Firmware does not run', 'Remove the BOOT0 jumper and reset to boot from user flash.'],
];

function DataTable({ columns, rows }) {
  return (
    <div className='stm-table-wrap'>
      <table>
        <thead>
          <tr>
            {columns.map((column) => (
              <th key={column}>{column}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.join('-')}>
              {row.map((cell, index) => (
                <td key={String(index) + cell}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function STM32G030ControllerBoard() {
  const { theme } = useContext(ThemeContext);

  return (
    <div className='stm-page' style={{ backgroundColor: theme.secondary, color: theme.tertiary }}>
      <Navbar />

      <main>
        <section className='stm-hero'>
          <Container className='stm-hero-inner'>
            <div className='stm-hero-copy'>
              <Link to='/#projects' className='stm-back-link' style={{ color: theme.primary }}>
                Back to projects
              </Link>
              <p className='stm-kicker' style={{ color: theme.primary }}>MS-G030 Wiki</p>
              <h1>MS-G030</h1>
              <p>
                A practical bring-up guide for the MS-G030 dev board. Version: Rev 0.1 / 2026. It covers USB-C power,
                CH340C USB-UART programming, SWD debug access, BOOT0 control, reset handling, and
                two 16-pin expansion headers.
              </p>
              <div className='stm-hero-actions'>
                <a href='#programming' style={{ backgroundColor: theme.primary, color: theme.secondary }}>
                  Programming
                </a>
                <a href='#pinout' style={{ borderColor: theme.primary, color: theme.primary }}>
                  Pinout
                </a>
              </div>
            </div>

            <div className='stm-board-visual' aria-label='MS-G030 controller board signal map'>
              <div className='stm-rail top'>USB-C 5 V</div>
              <div className='stm-chip regulator'>AP2112K<br />3.3 V LDO</div>
              <div className='stm-chip mcu'>STM32G030<br />K8T6TR</div>
              <div className='stm-chip usb'>CH340C<br />USB-UART</div>
              <div className='stm-header left'>J4 GPIO</div>
              <div className='stm-header right'>J5 GPIO</div>
              <div className='stm-rail bottom'>BOOT0 + RESET + SWD</div>
            </div>
          </Container>
        </section>

        <Container className='stm-content'>
          <section className='stm-section stm-summary-grid'>
            <article style={{ backgroundColor: theme.quaternary }}>
              <span>MCU</span>
              <strong>STM32G030K8T6TR</strong>
              <p>Arm Cortex-M0+ with 64 KB flash, powered from the board's 3.3 V rail.</p>
            </article>
            <article style={{ backgroundColor: theme.quaternary }}>
              <span>USB Interface</span>
              <strong>CH340C UART bridge</strong>
              <p>USB-C data is converted to 3.3 V TTL UART on PA2 and PA3.</p>
            </article>
            <article style={{ backgroundColor: theme.quaternary }}>
              <span>Boot Control</span>
              <strong>J2 BOOT0 jumper</strong>
              <p>Short J2 pins 1-2, then reset, to enter the STM32 ROM UART bootloader.</p>
            </article>
          </section>

          <section className='stm-section'>
            <div className='stm-section-heading'>
              <p style={{ color: theme.primary }}>First power-up</p>
              <h2>Bring-Up Checklist</h2>
            </div>
            <div className='stm-check-grid'>
              {checks.map((check, index) => (
                <div className='stm-check' key={check} style={{ borderColor: theme.primary }}>
                  <span>{index + 1}</span>
                  <p>{check}</p>
                </div>
              ))}
            </div>
            <DataTable columns={['Rail', 'Source', 'Expected Voltage']} rows={powerRails} />
          </section>

          <section className='stm-section' id='programming'>
            <div className='stm-section-heading'>
              <p style={{ color: theme.primary }}>Firmware loading</p>
              <h2>Programming Paths</h2>
            </div>
            <DataTable
              columns={['Method', 'Connector', 'BOOT0 State', 'Use Case']}
              rows={programmingOptions}
            />

            <div className='stm-two-column'>
              <article style={{ backgroundColor: theme.quaternary }}>
                <h3>USB-UART Bootloader</h3>
                <ol>
                  <li>Connect the board to the computer with USB-C.</li>
                  <li>Short J2 pins 1-2 to pull BOOT0 high.</li>
                  <li>Press SW1 to reset into the STM32 system bootloader.</li>
                  <li>Flash with STM32CubeProgrammer, stm32flash, or another UART bootloader tool.</li>
                  <li>Remove the jumper and reset again to boot the application.</li>
                </ol>
              </article>
              <article style={{ backgroundColor: theme.quaternary }}>
                <h3>SWD Debugging</h3>
                <p>
                  Use J3 with an ST-Link or compatible SWD probe. J3 exposes +3.3V reference,
                  SWDIO, SWCLK, BOOT0, and NRST. BOOT0 does not need to be high for normal SWD
                  flashing.
                </p>
              </article>
            </div>
          </section>

          <section className='stm-section' id='pinout'>
            <div className='stm-section-heading'>
              <p style={{ color: theme.primary }}>Expansion headers</p>
              <h2>J4 and J5 Pinout</h2>
            </div>
            <div className='stm-two-column stm-pinout'>
              <article style={{ backgroundColor: theme.quaternary }}>
                <h3>J4</h3>
                <DataTable columns={['Pin', 'Signal', 'Pin', 'Signal']} rows={j4Pins} />
              </article>
              <article style={{ backgroundColor: theme.quaternary }}>
                <h3>J5</h3>
                <DataTable columns={['Pin', 'Signal', 'Pin', 'Signal']} rows={j5Pins} />
              </article>
            </div>
            <p className='stm-note'>
              Re-check J5 pin 8 and the J5 pins 15-16 area against the original KiCad project before
              production wiring because the source drawing is visually tight in that region.
            </p>
          </section>

          <section className='stm-section'>
            <div className='stm-section-heading'>
              <p style={{ color: theme.primary }}>Debug notes</p>
              <h2>Troubleshooting</h2>
            </div>
            <DataTable columns={['Symptom', 'Checks']} rows={troubleshooting} />
          </section>
        </Container>
      </main>
    </div>
  );
}

export default STM32G030ControllerBoard;
